import { Option } from "@cca/components";
import {
  CourseItemType,
  CourseStatus,
  DayItemType,
  ProgressStatus,
  UserCourseStatus,
} from "@core/enums";
import type {
  AnnouncementDto,
  CourseDayBaseDto,
  CourseDayStructure,
  CourseDto,
  CourseOutlineDto,
  CourseProgressDayDto,
  CourseProgressDto,
  CourseStructure,
  CourseUserInfoDto,
  GetLearnerCoursesDto,
  LearnerDto,
  PaginationParams,
  QueryParams,
  SortingParams,
  SyllabusDto,
  UngradedWorkDto,
  UserCourseDto,
} from "@core/types";
import { CourseItemDto } from "@core/typesV2";
import { bindAll } from "@core/utils";
import { type NavigateFunction } from "react-router-dom";
import { z } from "zod";

import { GradebookStatus } from "../core/enums/gradebook";
import AxiosService, { type TokenHandlers } from "./axiosService";

export const courseStatusSchema = z.nativeEnum(CourseStatus);
export const userCourseStatusSchema = z.nativeEnum(UserCourseStatus);

export type GetCoursesByStatusParams = {
  courseStatus: CourseStatus;
};

export type GetLearnerCoursesByStatusParams = {
  courseStatus: UserCourseStatus;
};

export default class CourseService extends AxiosService {
  constructor(tokenHandlers: TokenHandlers, navigateTo: NavigateFunction) {
    super("courses", tokenHandlers, navigateTo);
    bindAll(this);
  }

  public getCoursesByStatus(
    status: CourseStatus,
    queryText: string | undefined,
  ): Promise<CourseDto[] | null> {
    const courses = this.get<CourseDto[], GetCoursesByStatusParams>("", {
      params: { courseStatus: status, query: queryText },
    });

    return courses;
  }

  public getUserCoursesByStatus(
    status: UserCourseStatus,
    queryText?: string,
  ): Promise<GetLearnerCoursesDto | null> {
    return this.get<GetLearnerCoursesDto, GetLearnerCoursesByStatusParams>(
      "my",
      {
        params: { courseStatus: status, query: queryText },
      },
    );
  }

  public getCourseById(id: number): Promise<CourseDto | null> {
    return this.get<CourseDto>(`${id}`);
  }

  public getUserCourseById(id: number): Promise<UserCourseDto | null> {
    return this.get<UserCourseDto>(`${id}/my`);
  }

  public getCourseUserBaseInfo(
    courseId: number,
    learnerId: string | undefined,
  ): Promise<CourseUserInfoDto | null> {
    return this.get<CourseUserInfoDto>(`${courseId}/learner/${learnerId}/info`);
  }

  public getCourseAnnouncements(
    courseId: number,
  ): Promise<AnnouncementDto[] | null> {
    return this.get<AnnouncementDto[]>(`${courseId}/announcements`);
  }

  public getCourseOutline(courseId: number): Promise<CourseOutlineDto | null> {
    return this.get<CourseOutlineDto>(`${courseId}/outline`);
  }

  public getCourseProgress(
    courseId: number,
  ): Promise<CourseProgressDto | null> {
    return this.get<CourseProgressDto>(`${courseId}/progress`);
  }

  public getCourseSyllabus(courseId: number): Promise<SyllabusDto | null> {
    return this.get<SyllabusDto>(`${courseId}/syllabus`);
  }

  public getCourseUngradedWorks(
    courseId: string,
    dayStatus: GradebookStatus,
    paginationParams: PaginationParams,
    params: SortingParams & QueryParams,
  ): Promise<UngradedWorkDto[] | null> {
    return this.get<UngradedWorkDto[]>(`${courseId}/ungraded-works`, {
      params: { dayStatus, ...paginationParams, ...params },
    });
  }

  public getLearnersWithUngradedWorks(
    courseId: string,
    dayItemId?: number,
    dayItemType?: DayItemType,
  ): Promise<Option[] | null> {
    return this.get<Option[]>(`${courseId}/ungraded-works/learners`, {
      params: { dayItemId, dayItemType },
    });
  }

  public getCourseLearners(
    courseId: string,
    paginationParams: PaginationParams,
    sortingParams: SortingParams,
    queryParams: QueryParams,
  ): Promise<LearnerDto[] | null> {
    return this.get<LearnerDto[]>(`${courseId}/learners`, {
      params: { ...paginationParams, ...sortingParams, ...queryParams },
    });
  }
}

export function getCourseStructureDays<TCourseDay extends CourseDayBaseDto>(
  courseStructure: CourseStructure<TCourseDay>,
) {
  return courseStructure.reduce((topLevelDays, item) => {
    if (item.type === "Day") return [...topLevelDays, item];

    const subitemDays = (item.subitems || []).reduce((nestedDays, subitem) => {
      if (subitem.type === "Day") return [...nestedDays, subitem];

      return [...nestedDays, ...subitem.days];
    }, [] as TCourseDay[]);

    return [...topLevelDays, ...subitemDays];
  }, [] as TCourseDay[]);
}
export function getCourseDays(items: CourseItemDto[]): CourseItemDto[] {
  const days: CourseItemDto[] = [];

  const traverse = (itemList: CourseItemDto[]) => {
    for (const item of itemList) {
      if (item.type === CourseItemType.Day) {
        days.push(item);
      }

      if (item.items?.length) {
        traverse(item.items);
      }
    }
  };

  traverse(items);
  return days;
}

export function* courseDayStructuresGenerator<
  TCourseDay extends CourseDayBaseDto,
>(
  courseStructure: CourseStructure<TCourseDay>,
): Generator<CourseDayStructure<TCourseDay>> {
  for (const item of courseStructure) {
    if (item.type === "Day") {
      yield { day: item };
    } else {
      for (const subitem of item.subitems || []) {
        if (subitem.type === "Day") {
          yield { day: subitem, unit: item };
        } else {
          for (const day of subitem.days) {
            yield { day, unit: item, topic: subitem };
          }
        }
      }
    }
  }
}

export function getDayInProgressStructure(
  courseStructure: CourseProgressDto,
): CourseDayStructure<CourseProgressDayDto> | undefined {
  for (const structure of courseDayStructuresGenerator(courseStructure)) {
    if (structure.day.progressStatus == ProgressStatus.InProgress) {
      return structure;
    }
  }
}
